import { type ReactNode, useEffect, useEffectEvent, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import logger from "@/lib/logging/logger";
import type {
	NativeBarcodeSymbology,
	NativeCameraBarcodePayload,
	NativeCameraTextPayload,
	NativeTextPreset,
	NativeCameraSlot as SlotRect,
} from "@/lib/native-bridge";
import type { CameraFacingMode } from "@/services/types";
import { describeNativeCameraError, NativeCameraError, nativeCamera } from "./nativeCamera";
import { toNativeFacing } from "./provider";
import { useNativeCameraSnapshot } from "./useNativeCamera";

// A ResizeObserver misses moves that keep the size (a title re-wrapping above the slot), and
// iPadOS can leave the layout viewport stale after an app switch, so the position is also polled.
const LAYOUT_POLL_MS = 250;
// A CSS transition on the frame would otherwise post a layout on every animation frame; posts
// are rate-limited and always end with the settled rect.
const LAYOUT_THROTTLE_MS = 50;
const RECT_TOLERANCE_PX = 0.5;
// The page is see-through only while a slot is mounted (index.css).
const SLOT_ACTIVE_ATTRIBUTE = "data-native-camera-slot-active";

type Hole = SlotRect & { borderRadius: string };

type NativeCameraSlotProps = {
	facing: CameraFacingMode;
	className?: string;
	detect?: NativeBarcodeSymbology[];
	detectText?: NativeTextPreset[];
	torch?: boolean;
	onBarcode?: (barcode: NativeCameraBarcodePayload) => void;
	onText?: (text: NativeCameraTextPayload) => void;
	children?: ReactNode;
};

const measure = (element: HTMLElement): Hole => {
	const rect = element.getBoundingClientRect();
	return {
		x: rect.left,
		y: rect.top,
		width: rect.width,
		height: rect.height,
		borderRadius: getComputedStyle(element).borderRadius,
	};
};

const toSlot = ({ x, y, width, height }: Hole): SlotRect => ({ x, y, width, height });

const sameHole = (previous: Hole | null, next: Hole): boolean =>
	previous !== null &&
	Math.abs(previous.x - next.x) < RECT_TOLERANCE_PX &&
	Math.abs(previous.y - next.y) < RECT_TOLERANCE_PX &&
	Math.abs(previous.width - next.width) < RECT_TOLERANCE_PX &&
	Math.abs(previous.height - next.height) < RECT_TOLERANCE_PX &&
	previous.borderRadius === next.borderRadius;

/**
 * The see-through area the wrapper paints its camera preview under. Owns the native camera
 * session for the page it is mounted on and reports its viewport rect on every change. The
 * page's dark background is repainted around the slot by a fixed backdrop so nothing in the
 * layout covers the preview.
 */
export function NativeCameraSlot({
	facing,
	className,
	detect,
	detectText,
	torch = false,
	onBarcode,
	onText,
	children,
}: NativeCameraSlotProps) {
	const ref = useRef<HTMLDivElement>(null);
	const holeRef = useRef<Hole | null>(null);
	const lastLayoutAtRef = useRef(0);
	const trailingLayoutRef = useRef<number | null>(null);
	const [hole, setHole] = useState<Hole | null>(null);
	const { status } = useNativeCameraSnapshot();

	const startCamera = useEffectEvent((initial: Hole) => {
		nativeCamera
			.start({ facing: toNativeFacing(facing), aspectRatio: "4:3", detect, detectText, slot: toSlot(initial) })
			.catch((error: unknown) => {
				if (error instanceof NativeCameraError && error.code === "superseded") return;
				logger.warning("INFO", "Native camera failed to start", describeNativeCameraError(error));
			});
	});

	const syncLayout = useEffectEvent((force: boolean) => {
		const element = ref.current;
		if (!element) return;
		const next = measure(element);
		if (!force && sameHole(holeRef.current, next)) return;
		holeRef.current = next;
		setHole(next);
		const elapsed = performance.now() - lastLayoutAtRef.current;
		if (!force && elapsed < LAYOUT_THROTTLE_MS) {
			if (trailingLayoutRef.current === null) {
				trailingLayoutRef.current = window.setTimeout(() => {
					trailingLayoutRef.current = null;
					syncLayout(true);
				}, LAYOUT_THROTTLE_MS - elapsed);
			}
			return;
		}
		lastLayoutAtRef.current = performance.now();
		nativeCamera.layout(toSlot(next));
	});

	const handleBarcode = useEffectEvent((barcode: NativeCameraBarcodePayload) => {
		onBarcode?.(barcode);
	});

	const handleText = useEffectEvent((text: NativeCameraTextPayload) => {
		onText?.(text);
	});

	useLayoutEffect(() => {
		const element = ref.current;
		if (!element) return;
		nativeCamera.setSlotActive(true);
		document.documentElement.toggleAttribute(SLOT_ACTIVE_ATTRIBUTE, true);
		const initial = measure(element);
		holeRef.current = initial;
		setHole(initial);
		startCamera(initial);
		return () => {
			nativeCamera.stop();
			nativeCamera.setSlotActive(false);
			document.documentElement.toggleAttribute(SLOT_ACTIVE_ATTRIBUTE, false);
		};
	}, []);

	useEffect(() => {
		const element = ref.current;
		if (!element) return;
		const onChange = () => syncLayout(false);
		const observer = new ResizeObserver(onChange);
		observer.observe(element);
		const interval = window.setInterval(onChange, LAYOUT_POLL_MS);
		window.addEventListener("resize", onChange);
		window.addEventListener("orientationchange", onChange);
		window.addEventListener("pageshow", onChange);
		document.addEventListener("visibilitychange", onChange);
		window.visualViewport?.addEventListener("resize", onChange);
		window.visualViewport?.addEventListener("scroll", onChange);
		return () => {
			observer.disconnect();
			window.clearInterval(interval);
			if (trailingLayoutRef.current !== null) window.clearTimeout(trailingLayoutRef.current);
			window.removeEventListener("resize", onChange);
			window.removeEventListener("orientationchange", onChange);
			window.removeEventListener("pageshow", onChange);
			document.removeEventListener("visibilitychange", onChange);
			window.visualViewport?.removeEventListener("resize", onChange);
			window.visualViewport?.removeEventListener("scroll", onChange);
		};
	}, []);

	// Every camera.ready (first start, reconfigure, resume after an interruption) gets the current
	// slot and settings again: the wrapper keeps the device's zoom, focus mode and torch from the
	// previous session and promises none of them across a resume.
	useEffect(() => {
		if (status !== "ready") return;
		syncLayout(true);
		nativeCamera.configure({ torch, zoom: 1, focusMode: "continuous" });
	}, [status, torch]);

	useEffect(() => nativeCamera.onBarcode((barcode) => handleBarcode(barcode)), []);
	useEffect(() => nativeCamera.onText((text) => handleText(text)), []);

	return (
		<>
			<div
				ref={ref}
				className={className}
				data-native-camera-slot=""
			>
				{children}
			</div>
			{hole &&
				createPortal(
					// The viewport-sized wrapper clips the spread shadow that paints the background, so
					// WebKit never has to back a layer larger than the screen.
					<div
						aria-hidden
						data-native-camera-backdrop=""
						className="pointer-events-none fixed inset-0 -z-10 overflow-hidden"
					>
						<div
							data-native-camera-hole=""
							className="absolute shadow-[0_0_0_200vmax_theme(colors.zinc.900)]"
							style={{
								left: hole.x,
								top: hole.y,
								width: hole.width,
								height: hole.height,
								borderRadius: hole.borderRadius,
							}}
						/>
					</div>,
					document.body,
				)}
		</>
	);
}
